import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MlMap } from 'maplibre-gl';
import { ClipboardCopy, CornerDownLeft, Crosshair, ListPlus, Loader2 } from 'lucide-react';
import { cancelGoto } from '@/lib/cancelGoto';
import { sendGoto, sendGotoFirmware } from '@/lib/gotoSend';
import { sendCommand } from '@/hooks/useHubSocket';
import { log } from '@/lib/logger';
import { useToasts } from '@/store/toasts';
import { recordEvent } from '@/store/appEvents';
import { useFleet } from '@/store/fleet';
import { useGotoTargets, distMeters } from '@/store/gotoTargets';
import { useMapMode } from '@/store/mapMode';
import { useMapPick } from '@/store/mapPick';
import { useMeasure } from '@/store/measure';
import { useMission } from '@/store/mission';
import { useOperatorPos } from '@/store/operatorPos';
import { useSelection } from '@/store/selection';
import { getDistanceUnit, useSettings } from '@/store/settings';
import { useSwarm } from '@/store/swarm';
import { useTrailPrefs } from '@/store/trailPrefs';
import { swarmGoto } from '@/swarm/dispatch';
import { setActiveMap } from './context';
import { ManualOverlay } from './ManualOverlay';
import { MapModeBar } from './MapModeBar';
import { MeasureOverlay } from './MeasureOverlay';
import { prefetchAroundViewport } from './prefetch';
import { useMapBase } from './useMapBase';
import { ViewOptions } from './ViewOptions';
import { cn, fmtDistance, fmtLatLon } from '@/lib/utils';

const GOTO_REACHED_M = 4; // auto-clear marker once the asset is within 4 m

// Pull live values from the design tokens so the map matches the chrome.
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}
const COLOR_GOOD = cssVar('--color-status-good', '#54b35e');
const COLOR_WARN = cssVar('--color-status-warn', '#e3c14a');
const COLOR_OFFLINE = cssVar('--color-status-offline', '#6a6d73');
const COLOR_INFLIGHT = cssVar('--color-accent', '#4fc8e0');
const COLOR_ACCENT = cssVar('--color-accent', '#4fc8e0');
const COLOR_AMBER = cssVar('--color-amber', '#e8a83a');

function statusColor(state: { connection: string; arm: string }): string {
  if (state.connection === 'disconnected' || state.connection === 'lost') return COLOR_OFFLINE;
  if (state.connection === 'degraded') return COLOR_WARN;
  if (state.arm === 'armed') return COLOR_INFLIGHT;
  return COLOR_GOOD;
}

interface RobotMarker {
  el: HTMLDivElement;
  marker: maplibregl.Marker;
  /** Cached child refs — querySelector per frame is what killed marker FPS. */
  ring: SVGElement | null;
  chev: SVGElement | null;
  lbl: HTMLElement | null;
  /** Last-applied values so unchanged frames skip DOM writes entirely. */
  lastColor: string;
  lastName: string;
  lastSelected: boolean;
  lastYawDeg: number;
}

/** Mission-route source + layer. Amber matches the waypoint marker colour
 *  so the route reads as one entity; an outer halo gives readability on
 *  any basemap (light or dark). */
const WP_ROUTE_COLOR = '#F59E0B';
const WP_ROUTE_HALO = '#0e0f11';
const WP_COLOR = '#F59E0B';
const WP_COLOR_SELECTED = '#22C55E';

function ensureMissionLayer(m: MlMap) {
  if (!m.isStyleLoaded()) return;
  if (!m.getSource('pilot-mission-route')) {
    m.addSource('pilot-mission-route', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  // Source for the direction arrows — a Point per midpoint with `bearing`.
  if (!m.getSource('pilot-mission-arrows')) {
    m.addSource('pilot-mission-arrows', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  // Halo for legibility on busy basemaps.
  if (!m.getLayer('pilot-mission-route-halo')) {
    m.addLayer({
      id: 'pilot-mission-route-halo',
      type: 'line',
      source: 'pilot-mission-route',
      paint: {
        'line-color': WP_ROUTE_HALO,
        'line-width': 4,
        'line-opacity': 0.55,
      },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
  }
  if (!m.getLayer('pilot-mission-route-line')) {
    m.addLayer({
      id: 'pilot-mission-route-line',
      type: 'line',
      source: 'pilot-mission-route',
      paint: {
        'line-color': WP_ROUTE_COLOR,
        'line-width': 2,
        'line-opacity': 0.9,
      },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
  }
  // Direction arrows. Drawn as small triangles rotated by per-feature bearing.
  // No glyph dependency — pure SVG path expression keeps it font-free.
  if (!m.getLayer('pilot-mission-arrows-layer')) {
    m.addLayer({
      id: 'pilot-mission-arrows-layer',
      type: 'symbol',
      source: 'pilot-mission-arrows',
      layout: {
        'icon-image': 'wp-direction-arrow',
        'icon-allow-overlap': true,
        'icon-rotate': ['get', 'bearing'],
        'icon-rotation-alignment': 'map',
        'icon-size': 0.9,
      },
    });
  }
}

/** Forward bearing from (lat1,lon1) to (lat2,lon2) in degrees, 0 = North.
 *  Pure spherical geometry, good enough at human-eye scale. */
function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Tiny triangle PNG (data URL) used as the direction-arrow icon. Created
 *  once and registered on the map via `map.addImage`. Amber on transparent
 *  so the arrow rides on top of the line cleanly. */
function ensureArrowImage(m: MlMap) {
  if (m.hasImage('wp-direction-arrow')) return;
  const size = 16;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);
  // Triangle pointing UP (north). icon-rotate rotates clockwise by bearing.
  ctx.fillStyle = WP_ROUTE_COLOR;
  ctx.strokeStyle = WP_ROUTE_HALO;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(size / 2, 1.5);
  ctx.lineTo(size - 2, size - 2);
  ctx.lineTo(2, size - 2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  const img = ctx.getImageData(0, 0, size, size);
  m.addImage('wp-direction-arrow', {
    width: size,
    height: size,
    data: new Uint8Array(img.data),
  });
}

/** Dashed amber drone→GOTO-target line; the distance chip rides its midpoint
 *  as an HTML marker (font-free — no glyph dependency on the basemap style). */
function ensureGotoLineLayer(m: MlMap) {
  if (!m.isStyleLoaded()) return;
  if (!m.getSource('goto-lines')) {
    m.addSource('goto-lines', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!m.getLayer('goto-lines-line')) {
    m.addLayer({
      id: 'goto-lines-line',
      type: 'line',
      source: 'goto-lines',
      paint: {
        // Amber = GOTO legs; green = an active RTL return (per feature).
        'line-color': ['coalesce', ['get', 'color'], COLOR_AMBER],
        'line-width': 1.5,
        'line-dasharray': [2, 2],
        'line-opacity': 0.75,
      },
    });
  }
}

/** Distance-label element for a GOTO leg: dark pill, amber mono text,
 *  rotated parallel to the leg by `orientLegChip`. `dim` = completed leg. */
function makeLegChipEl(dim: boolean, color = COLOR_AMBER): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'fabricos-goto-line-chip';
  el.style.pointerEvents = 'none';
  const inner = document.createElement('span');
  inner.style.cssText = [
    'display:inline-block',
    'white-space:nowrap',
    'font-family:var(--font-mono)',
    'font-size:9.5px',
    'letter-spacing:0.1em',
    `color:${color}`,
    'background:rgba(14,15,17,0.85)',
    `border:1px solid ${color}${dim ? '33' : '59'}`,
    'border-radius:2px',
    'padding:1px 5px',
    'pointer-events:none',
    ...(dim ? ['opacity:0.55'] : []),
  ].join(';');
  el.appendChild(inner);
  return el;
}

/** Rotate a leg chip's text parallel to the a→b screen segment, flipped so
 *  it never reads upside-down, lifted 9 px off the stroke. */
function orientLegChip(
  m: MlMap,
  chip: maplibregl.Marker,
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
  text: string,
) {
  const inner = chip.getElement().firstElementChild as HTMLElement | null;
  if (!inner) return;
  if (inner.textContent !== text) inner.textContent = text;
  const p1 = m.project([a.lon, a.lat]);
  const p2 = m.project([b.lon, b.lat]);
  let deg = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
  if (deg > 90) deg -= 180;
  if (deg < -90) deg += 180;
  inner.style.transform = `rotate(${deg.toFixed(1)}deg) translateY(-9px)`;
}

/** Ensure trails source + layer exist on the current style. Idempotent. */
function ensureTrailsLayer(m: MlMap) {
  if (!m.isStyleLoaded()) return;
  if (!m.getSource('trails')) {
    m.addSource('trails', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  // Halo under the accent line: readable on any basemap, and round joins
  // stop the polyline from looking segmented at 4 Hz point spacing.
  if (!m.getLayer('trails-halo')) {
    m.addLayer({
      id: 'trails-halo',
      type: 'line',
      source: 'trails',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#0e0f11',
        'line-width': 4,
        'line-opacity': 0.55,
      },
    });
  }
  if (!m.getLayer('trails-line')) {
    m.addLayer({
      id: 'trails-line',
      type: 'line',
      source: 'trails',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // Per-asset colour from the feature (sidebar extras panel).
        'line-color': ['coalesce', ['get', 'color'], COLOR_ACCENT],
        'line-width': 2,
        'line-opacity': 0.8,
      },
    });
  }
  // Respect the View-options Trail toggle (also re-applied after style swaps).
  const trailVis = useSettings.getState().map.showTrails !== false ? 'visible' : 'none';
  m.setLayoutProperty('trails-halo', 'visibility', trailVis);
  m.setLayoutProperty('trails-line', 'visibility', trailVis);
  // Repaint trails from current store right after re-adding the source — avoids
  // a one-tick gap where the line vanishes after a style swap.
  const src = m.getSource('trails') as maplibregl.GeoJSONSource | undefined;
  if (src) {
    src.setData(trailFeatures(useFleet.getState().trails));
  }
}

/** Trails → GeoJSON, skipping per-asset-hidden ones (sidebar Route toggle)
 *  and carrying each asset's chosen colour for the data-driven line paint. */
function trailFeatures(
  trails: Record<string, { lat: number; lon: number }[]>,
): GeoJSON.FeatureCollection {
  const { hidden, colors } = useTrailPrefs.getState();
  return {
    type: 'FeatureCollection',
    features: Object.entries(trails)
      .filter(([id]) => !hidden[id])
      .map(([id, pts]) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: pts.map((p) => [p.lon, p.lat]),
        },
        properties: { color: colors[id] ?? COLOR_ACCENT },
      })),
  };
}

export function MapView() {
  const markersRef = useRef<globalThis.Map<string, RobotMarker>>(new globalThis.Map());
  const gotoQueueMarkersRef = useRef<maplibregl.Marker[]>([]);
  const gotoMarkersRef = useRef<globalThis.Map<string, maplibregl.Marker>>(
    new globalThis.Map(),
  );
  const waypointMarkersRef = useRef<
    globalThis.Map<number, { el: HTMLDivElement; marker: maplibregl.Marker }>
  >(new globalThis.Map());
  const [loading, setLoading] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; lat: number; lon: number } | null>(
    null,
  );
  /** Per-robot distance chip riding the drone→target line midpoint. */
  const gotoLineLabelsRef = useRef<globalThis.Map<string, maplibregl.Marker>>(new globalThis.Map());
  /** Persistent leg-distance chips for COMPLETED gotos (from history) —
   *  keyed `${robotId}:${ts_ms}`. Live/die with the trail toggle. */
  const doneLegChipsRef = useRef<globalThis.Map<string, maplibregl.Marker>>(new globalThis.Map());
  /** Per-asset RTL point (firmware HOME_POSITION) markers, keyed robotId. */
  const rtlMarkersRef = useRef<globalThis.Map<string, maplibregl.Marker>>(new globalThis.Map());
  const hoverHudRef = useRef<HTMLDivElement | null>(null);
  const mode = useMapMode((s) => s.mode);
  const pickActive = useMapPick((s) => s.active != null);
  // Esc cancels an armed point-pick.
  useEffect(() => {
    if (!pickActive) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') useMapPick.getState().cancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickActive]);
  // Cursor cue: each mode gets a glyph so the operator's hand-eye stays
  // honest about what the next click will do.
  const cursorClass =
    pickActive ? 'cursor-crosshair'
    : mode === 'direct' ? 'cursor-crosshair'
    : mode === 'waypoint' ? 'cursor-cell'
    : mode === 'measure' ? 'cursor-crosshair'
    : 'cursor-default';

  const { containerRef, mapRef } = useMapBase({
    showScale: true,
    onReady: (m) => {
      // Bump global tile fetch concurrency from the default 16 → 24 for snappier
      // pan/zoom and faster provider swaps. Set once per page.
      (maplibregl as unknown as { setMaxParallelImageRequests?: (n: number) => void })
        .setMaxParallelImageRequests?.(24);
      // Register the active map so cross-tree consumers (Settings → Pre-cache
      // button) can find it without prop-drilling.
      setActiveMap(m);

      m.on('dataloading', () => setLoading(true));
      m.on('idle', () => {
        setLoading(false);
        prefetchAroundViewport(m, 2);
      });
      m.on('error', () => setLoading(false));

      // Right-click → command menu (Normal + Direct modes). Deliberate,
      // point-anchored commands that work even from the inert Normal mode.
      m.on('contextmenu', (e) => {
        e.preventDefault();
        const curMode = useMapMode.getState().mode;
        if (curMode !== 'direct' && curMode !== 'measure') return;
        useMapMode.getState().touch();
        setCtxMenu({ x: e.point.x, y: e.point.y, lat: e.lngLat.lat, lon: e.lngLat.lng });
      });
      m.on('movestart', () => {
        setCtxMenu(null);
        // Pan/zoom/rotate counts as activity for the Direct idle guard.
        if (useMapMode.getState().mode === 'direct') useMapMode.getState().touch();
      });

      // Hover HUD — cursor coords + live distance/bearing from the selected
      // drone. Direct DOM writes (no React state): mousemove is way too hot
      // for setState.
      m.on('mousemove', (e) => {
        const curMode = useMapMode.getState().mode;
        // Cursor moving over the map = the operator is engaged; hold the
        // Direct idle guard open.
        if (curMode === 'direct') useMapMode.getState().touch();
        const hud = hoverHudRef.current;
        if (!hud) return;
        if (
          useSettings.getState().map.showHoverInfo === false ||
          (curMode !== 'direct' && curMode !== 'measure')
        ) {
          hud.style.display = 'none';
          return;
        }
        const sel = useSelection.getState().selected;
        const robotId = sel.size === 1 ? (sel.values().next().value as string) : null;
        const pos = robotId ? useFleet.getState().states[robotId]?.position : undefined;
        let text = fmtLatLon(e.lngLat.lat, e.lngLat.lng);
        if (pos && robotId) {
          const d = distMeters(pos, { lat: e.lngLat.lat, lon: e.lngLat.lng });
          const brg = Math.round(bearingDeg(pos.lat, pos.lon, e.lngLat.lat, e.lngLat.lng));
          const unit = getDistanceUnit(useSettings.getState());
          const name = useFleet.getState().robots[robotId]?.name ?? robotId;
          text += `  ·  ${fmtDistance(d, unit)} / ${brg}° from ${name}`;
        }
        hud.textContent = text;
        // Ride WITH the cursor (operator: eyes shouldn't drop to a corner).
        // Flip to the left of the pointer near the right edge.
        const w = m.getCanvas().clientWidth;
        const flip = e.point.x > w - 240;
        hud.style.left = `${e.point.x + (flip ? -14 : 14)}px`;
        hud.style.top = `${e.point.y + 16}px`;
        hud.style.transform = flip ? 'translateX(-100%)' : '';
        hud.style.display = 'block';
      });
      m.getCanvasContainer().addEventListener('mouseleave', () => {
        if (hoverHudRef.current) hoverHudRef.current.style.display = 'none';
      });
      m.on('click', (e) => {
        setCtxMenu(null);
        // Armed point-pick (Set RTL point, …) eats the click before ANY
        // mode logic — a pick must never double as a GOTO.
        const pick = useMapPick.getState().take();
        if (pick) {
          pick(e.lngLat.lat, e.lngLat.lng);
          flashClickMarker(m, [e.lngLat.lng, e.lngLat.lat]);
          return;
        }
        const curMode = useMapMode.getState().mode;
        const target = e.originalEvent?.target as HTMLElement | null;
        // Skip clicks that originated on existing markers — those have their
        // own handlers (select asset / delete waypoint).
        if (target && target.closest('.fabricos-marker, .fabricos-wp-marker')) return;

        if (curMode === 'waypoint') {
          useMission.getState().addWaypointAt(e.lngLat.lat, e.lngLat.lng);
          useSelection.getState().setFocus('waypoint');
          log.mission('waypoint added', { lat: e.lngLat.lat, lon: e.lngLat.lng });
          return;
        }
        if (curMode === 'measure') {
          // Range point only — never a command. The overlay does the math.
          useMeasure.getState().set({ lat: e.lngLat.lat, lon: e.lngLat.lng });
          return;
        }
        if (curMode !== 'direct') return;
        useMapMode.getState().touch();
        const selection = useSelection.getState().selected;
        if (selection.size === 0) return;
        const { lat, lng } = e.lngLat;
        const states = useFleet.getState().states;
        const targetAlt = useGotoTargets.getState().settings.defaultAltAglM;

        if (selection.size === 1) {
          const robotId = Array.from(selection)[0]!;
          // Plain click: fly there now (replaces the active target).
          // CTRL+click: queue — finish the current GOTO first, then this one.
          // Ctrl, not shift: MapLibre's box zoom owns shift+mousedown and
          // suppresses even a zero-drag shift+click (DOM.suppressClick in
          // BoxZoomHandler.mouseupWindow), so a shift-modified click never
          // reaches this handler. Ctrl only rides drag-rotate, which
          // suppresses clicks solely after real movement — a clean
          // ctrl+click arrives intact. shiftKey kept as a fallback in case
          // box zoom is ever disabled. sendGoto owns confirm rules, marker,
          // queue and history.
          sendGoto(robotId, lat, lng, {
            append: (e.originalEvent?.ctrlKey || e.originalEvent?.shiftKey) ?? false,
          });
        } else {
          const cfg = useSwarm.getState();
          const drones = Array.from(selection)
            .map((id) => {
              const s = states[id];
              if (!s?.position) return null;
              return {
                robotId: id,
                lat: s.position.lat,
                lon: s.position.lon,
                alt_m: s.position.alt_rel_home_m ?? 10,
              };
            })
            .filter((d): d is NonNullable<typeof d> => d !== null);
          if (drones.length >= 2) {
            swarmGoto(
              drones,
              { lat, lon: lng, alt_m: targetAlt },
              cfg.formation,
              cfg.spacing,
              cfg.altStagger,
            );
            // For the swarm case we mark the rally point with one target
            // per participating robot; markers cluster naturally at the
            // formation centre and resolve to per-robot positions as each
            // unit reaches its slot.
            const ts = Date.now();
            for (const d of drones) {
              useGotoTargets.getState().set({
                robotId: d.robotId,
                lat,
                lon: lng,
                alt_m: targetAlt,
                ts_ms: ts,
              });
            }
          }
        }
        flashClickMarker(m, [lng, lat]);
      });
    },
    // After every style swap MapLibre wipes overlays — re-add the trails layer.
    onStyleLoaded: (m) => {
      ensureTrailsLayer(m);
      ensureArrowImage(m);
      ensureMissionLayer(m);
      ensureGotoLineLayer(m);
    },
    onDestroy: () => setActiveMap(null),
  });

  // (Direct-mode idle auto-exit lives in MapModeBar — it owns the countdown
  // chip. This component only reports activity via useMapMode.touch().)

  // Home (settings default view / HQ) + pilot (browser geolocation) markers.
  // Both are passive landmarks — no interactions beyond a hover title.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    let homeMk: maplibregl.Marker | null = null;
    let pilotMk: maplibregl.Marker | null = null;

    function sync() {
      if (!m) return;
      const s = useSettings.getState().map;
      if (!homeMk) {
        const el = document.createElement('div');
        el.title = 'Home position (Settings → Map)';
        el.style.cssText =
          'display:grid;place-items:center;width:22px;height:22px;border-radius:50%;' +
          'background:rgba(14,15,17,0.85);border:1.5px solid #e8a83a;box-sizing:border-box;z-index:10;';
        el.innerHTML =
          `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e8a83a" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>`;
        // setLngLat BEFORE addTo — addTo triggers a marker update that
        // reads .lng, so an unpositioned marker crashes the map.
        homeMk = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([s.initialLon, s.initialLat])
          .addTo(m);
      }
      homeMk.setLngLat([s.initialLon, s.initialLat]);

      const pilot = useOperatorPos.getState().pos;
      if (pilot) {
        if (!pilotMk) {
          const el = document.createElement('div');
          el.title = 'Pilot — my location (browser)';
          el.style.cssText =
            'display:grid;place-items:center;width:20px;height:20px;border-radius:50%;' +
            'background:rgba(14,15,17,0.85);border:1.5px solid #4fc8e0;box-sizing:border-box;z-index:10;';
          el.innerHTML =
            `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4fc8e0" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></svg>`;
          pilotMk = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([pilot.lon, pilot.lat])
            .addTo(m);
        }
        pilotMk.setLngLat([pilot.lon, pilot.lat]);
      } else if (pilotMk) {
        pilotMk.remove();
        pilotMk = null;
      }
    }

    sync();
    const unsubs = [useSettings.subscribe(sync), useOperatorPos.subscribe(sync)];
    return () => {
      unsubs.forEach((u) => u());
      homeMk?.remove();
      pilotMk?.remove();
    };
  }, []);

  // Flip trail-layer visibility live when the View-options toggle changes.
  useEffect(() => {
    return useSettings.subscribe((s) => {
      const m = mapRef.current;
      if (!m) return;
      const vis = s.map.showTrails !== false ? 'visible' : 'none';
      for (const layer of ['trails-halo', 'trails-line']) {
        if (m.getLayer(layer)) m.setLayoutProperty(layer, 'visibility', vis);
      }
    });
  }, []);

  // ------------------------------------------------------------------
  // Fleet → map painter. Telemetry ticks (≈18/s per drone) only mark the
  // frame dirty; ALL map writes happen in one requestAnimationFrame loop:
  //   - marker positions interpolate toward the latest telemetry fix
  //     (exponential ease, 60 fps) so icons glide instead of stepping at
  //     the 4 Hz position rate;
  //   - DOM writes (colour, label, ring, yaw) are diffed against cached
  //     values — unchanged markers cost nothing;
  //   - trails re-upload only when the trail data actually changed AND
  //     the layer is visible;
  //   - the whole frame is guarded: one bad frame logs and skips instead
  //     of killing the subscription (the "icons frozen forever" failure).
  // ------------------------------------------------------------------
  useEffect(() => {
    /** Interpolated display position per robot (lat/lon/yaw°). */
    const disp = new globalThis.Map<string, { lat: number; lon: number; yaw: number }>();
    let fleetDirty = true;
    let rafId: number | null = null;
    let lastT = performance.now();
    let lastUploadedTrails: unknown = null;

    const EASE_RATE = 6; // 1/s — ~150 ms to close 60% of the gap
    const SNAP_M = 150; // teleports (spawn, respawn) snap instead of glide
    const POS_EPS = 1e-7; // ≈1 cm in degrees — below this, skip DOM writes
    // Whether the goto-lines source currently holds features — lets idle
    // frames skip the setData entirely (same trick as the measure line).
    let gotoLinesDrawn = false;

    function shortestAngle(from: number, to: number): number {
      return ((to - from + 540) % 360) - 180;
    }

    function frame(t: number) {
      rafId = null;
      const m = mapRef.current;
      if (!m) return;
      const dt = Math.min((t - lastT) / 1000, 0.25);
      lastT = t;
      let needMore = false;
      let followCenter: [number, number] | null = null;

      try {
        const state = useFleet.getState();
        const selState = useSelection.getState();
        const selection = selState.selected;
        const markers = markersRef.current;
        const k = 1 - Math.exp(-dt * EASE_RATE);

        for (const robotId of Object.keys(state.states)) {
          const rs = state.states[robotId];
          if (!rs || !rs.position) continue;
          const target = {
            lat: rs.position.lat,
            lon: rs.position.lon,
            yaw: rs.attitude ? ((rs.attitude.yaw_rad * 180) / Math.PI + 360) % 360 : 0,
          };
          const isSelected = selection.has(robotId);
          const name = state.robots[robotId]?.name ?? robotId;

          let entry = markers.get(robotId);
          if (!entry) {
            const el = document.createElement('div');
            el.className = 'fabricos-marker';
            el.style.cssText =
              'position:relative;width:24px;height:24px;display:grid;place-items:center;cursor:pointer;z-index:40;';
            el.title = robotId;
            el.innerHTML = `
              <svg class="ring" style="position:absolute;inset:-4px;display:none;pointer-events:none" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="14" fill="none" stroke="${COLOR_ACCENT}" stroke-opacity="0.55" stroke-width="1" />
              </svg>
              <svg class="chevron" width="16" height="16" viewBox="0 0 16 16" style="transform-origin:center">
                <path d="M8 1 L13 14 L8 10.5 L3 14 Z" fill="currentColor" stroke="#0e0f11" stroke-width="0.6" />
              </svg>
              <span class="lbl" style="position:absolute;left:50%;top:calc(100% + 2px);transform:translateX(-50%);
                white-space:nowrap;font-family:var(--font-sans);font-size:10px;
                background:rgba(14,15,17,.72);padding:1px 5px;border-radius:2px;
                border:1px solid var(--color-border-subtle);"></span>
            `;
            el.addEventListener('click', (e) => {
              e.stopPropagation();
              useSelection.getState().select(robotId, { multi: (e as MouseEvent).shiftKey });
            });
            const marker = new maplibregl.Marker({ element: el }).setLngLat([
              target.lon,
              target.lat,
            ]);
            marker.addTo(m);
            entry = {
              el,
              marker,
              ring: el.querySelector('.ring'),
              chev: el.querySelector('.chevron'),
              lbl: el.querySelector('.lbl'),
              lastColor: '',
              lastName: '',
              lastSelected: false,
              lastYawDeg: NaN,
            };
            markers.set(robotId, entry);
            disp.set(robotId, { ...target });
          }

          // Interpolate displayed pose toward the telemetry fix.
          let d = disp.get(robotId);
          if (!d || distMeters(d, target) > SNAP_M) {
            d = { ...target };
            disp.set(robotId, d);
          } else {
            const dLat = target.lat - d.lat;
            const dLon = target.lon - d.lon;
            if (Math.abs(dLat) > POS_EPS || Math.abs(dLon) > POS_EPS) {
              d.lat += dLat * k;
              d.lon += dLon * k;
              needMore = true;
            } else {
              d.lat = target.lat;
              d.lon = target.lon;
            }
            const dYaw = shortestAngle(d.yaw, target.yaw);
            if (Math.abs(dYaw) > 0.3) {
              d.yaw = (d.yaw + dYaw * k + 360) % 360;
              needMore = true;
            } else {
              d.yaw = target.yaw;
            }
          }
          entry.marker.setLngLat([d.lon, d.lat]);
          if (isSelected && selState.followSelected && selection.size === 1) {
            followCenter = [d.lon, d.lat];
          }

          // Diffed DOM writes.
          const color = statusColor(rs);
          if (entry.lastColor !== color) {
            entry.el.style.color = color;
            entry.lastColor = color;
          }
          if (entry.lastSelected !== isSelected) {
            if (entry.ring) entry.ring.style.display = isSelected ? 'block' : 'none';
            if (entry.lbl) {
              entry.lbl.style.color = isSelected
                ? 'var(--color-fg-primary)'
                : 'var(--color-fg-secondary)';
            }
            entry.lastSelected = isSelected;
          }
          if (entry.lastName !== name && entry.lbl) {
            entry.lbl.textContent = name;
            entry.lastName = name;
          }
          if (entry.chev && Math.abs((entry.lastYawDeg || 0) - d.yaw) > 0.3) {
            entry.chev.style.transform = `rotate(${d.yaw}deg)`;
            entry.lastYawDeg = d.yaw;
          }
        }

        // The rest only needs to run when fresh telemetry arrived.
        if (fleetDirty) {
          // Remove markers for robots no longer present.
          for (const id of Array.from(markers.keys())) {
            if (!state.states[id] && !state.robots[id]) {
              markers.get(id)?.marker.remove();
              markers.delete(id);
              disp.delete(id);
            }
          }

          // Trails — upload only on change, only while visible.
          if (
            useSettings.getState().map.showTrails !== false &&
            state.trails !== lastUploadedTrails
          ) {
            const src = m.getSource('trails') as maplibregl.GeoJSONSource | undefined;
            if (src) {
              src.setData(trailFeatures(state.trails));
              lastUploadedTrails = state.trails;
            }
          }

          // Per-asset RTL point: the firmware home (HOME_POSITION), which is
          // what RTL returns to. Defaults to the arming spot. Labelled green
          // ring; DRAG it to move the point (sends DO_SET_HOME on release).
          {
            const rtlMarkers = rtlMarkersRef.current;
            const seen = new Set<string>();
            for (const [robotId, rs] of Object.entries(state.states)) {
              const home = rs?.home;
              if (!home) continue;
              seen.add(robotId);
              const name = state.robots[robotId]?.name ?? robotId;
              let mk = rtlMarkers.get(robotId);
              if (!mk) {
                const el = document.createElement('div');
                el.title = `RTL point for ${name}. Drag to move it.`;
                el.style.cssText =
                  'position:relative;display:grid;place-items:center;z-index:10;cursor:grab;';
                el.innerHTML =
                  `<span style="display:grid;place-items:center;width:20px;height:20px;border-radius:50%;` +
                  `background:rgba(14,15,17,0.85);border:1.5px solid #54b35e;box-sizing:border-box;">` +
                  `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#54b35e" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg></span>` +
                  `<span style="position:absolute;top:calc(100% + 2px);left:50%;transform:translateX(-50%);` +
                  `white-space:nowrap;font-family:var(--font-mono);font-size:9px;letter-spacing:0.1em;` +
                  `text-transform:uppercase;color:#54b35e;background:rgba(14,15,17,0.82);padding:1px 4px;` +
                  `border-radius:2px;">RTL · ${name}</span>`;
                mk = new maplibregl.Marker({ element: el, anchor: 'center', draggable: true })
                  .setLngLat([home.lon, home.lat])
                  .addTo(m);
                mk.on('dragstart', () => {
                  el.dataset.dragging = '1';
                });
                mk.on('dragend', () => {
                  delete el.dataset.dragging;
                  const p = mk!.getLngLat();
                  sendCommand({
                    kind: 'set_home',
                    id: `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
                    robot_id: robotId,
                    priority: 'normal',
                    lat: p.lat,
                    lon: p.lng,
                  });
                  useToasts.getState().push({
                    tone: 'good',
                    title: `RTL point moved for ${name}`,
                    body: `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}`,
                    durationMs: 4000,
                  });
                  recordEvent('pilot', `RTL point dragged for ${name}`, { robotId, robotName: name });
                });
                rtlMarkers.set(robotId, mk);
              }
              // Don't fight an in-progress drag; the firmware's next
              // HOME_POSITION broadcast is the authoritative position.
              if (mk.getElement().dataset.dragging !== '1') {
                mk.setLngLat([home.lon, home.lat]);
              }
            }
            for (const id of Array.from(rtlMarkers.keys())) {
              if (!seen.has(id)) {
                rtlMarkers.get(id)?.remove();
                rtlMarkers.delete(id);
              }
            }
          }

          // Soft-geofence colouring — per frame so late-arriving home or a
          // radius edit recolours live. Shows WHY on the label:
          // "⚠ 2.31 km from home · fence 1000 m".
          {
            const fenceR = useGotoTargets.getState().settings.fenceRadiusM;
            const unit = getDistanceUnit(useSettings.getState());
            for (const [robotId, tgt] of Object.entries(useGotoTargets.getState().targets)) {
              const mk = gotoMarkersRef.current.get(robotId);
              if (!mk) continue;
              const root = mk.getElement();
              const home = state.states[robotId]?.home;
              const dHome = home && fenceR > 0 ? distMeters(home, tgt) : null;
              const outside = dHome != null && dHome > fenceR;
              if (root.dataset.fence === String(outside) && !outside) continue;
              root.dataset.fence = String(outside);
              const ringEl = root.querySelector('.goto-ring') as HTMLElement | null;
              if (ringEl) {
                ringEl.style.borderColor = outside ? '#e5484d' : COLOR_AMBER;
                ringEl.style.opacity = outside ? '0.95' : '0.65';
                ringEl.style.boxShadow = outside ? '0 0 6px 1px rgba(229,72,77,0.55)' : '';
              }
              const labelEl = root.querySelector('.goto-label') as HTMLElement | null;
              if (labelEl) {
                labelEl.style.borderColor = outside
                  ? 'rgba(229,72,77,0.65)'
                  : 'rgba(232,168,58,0.45)';
              }
              const fenceEl = root.querySelector('.goto-label-fence') as HTMLElement | null;
              if (fenceEl) {
                fenceEl.style.display = outside ? 'block' : 'none';
                if (outside && dHome != null) {
                  const txt = `⚠ ${fmtDistance(dHome, unit)} from home · fence ${fmtDistance(fenceR, unit)}`;
                  if (fenceEl.textContent !== txt) fenceEl.textContent = txt;
                }
              }
            }
          }

          // Auto-advance GOTO targets once the asset has arrived.
          // `advance()` (NOT `clear()`) promotes the queue head — shift+click
          // queueing depends on this — then the next leg's firmware command
          // fires here.
          const targets = useGotoTargets.getState().targets;
          for (const [robotId, tgt] of Object.entries(targets)) {
            const rs = state.states[robotId];
            if (rs?.position && distMeters(rs.position, tgt) <= GOTO_REACHED_M) {
              useGotoTargets.getState().advance(robotId);
              const next = useGotoTargets.getState().targets[robotId];
              if (next) sendGotoFirmware(next);
              // Arrival is a first-class app event (operator: "first event
              // will be when goto mode drone reached goto location").
              const name = state.robots[robotId]?.name ?? robotId;
              recordEvent(
                'pilot',
                `${name} reached GOTO target (${tgt.lat.toFixed(5)}, ${tgt.lon.toFixed(5)})${next ? ' — continuing to next queued leg' : ''}`,
                { robotId, robotName: name },
              );
              // Also into the ROBOT event stream so it shows in the right
              // panel's Events feed exactly like battery/ack events do.
              useFleet.getState().pushEvent({
                kind: 'text',
                robot_id: robotId,
                // ts-rs exports u64 as bigint; the feed formats it via Number().
                ts_ms: BigInt(Date.now()),
                severity: 'info',
                message: `Reached GOTO target (${tgt.lat.toFixed(5)}, ${tgt.lon.toFixed(5)})${next ? ' — continuing to next queued leg' : ''}`,
              });
              useToasts.getState().push({
                tone: 'good',
                title: `${name} reached GOTO target`,
                body: next ? 'Continuing to the next queued leg.' : undefined,
                durationMs: 4000,
              });
            }
          }

          // Drone→target line + leg distance labels ride the same frame.
          // The line starts at the INTERPOLATED marker position so it glides
          // with the icon instead of stepping at the telemetry rate.
          //
          // Zoom declutter (Settings → Map → Assets): zoomed out past
          // `legLabelMergeZoom` the per-leg labels collapse into ONE total
          // per asset; past `legLabelHideZoom` no labels render at all.
          {
            const gotoSt = useGotoTargets.getState();
            const targets2 = gotoSt.targets;
            const ids = Object.keys(targets2);
            const chips = gotoLineLabelsRef.current;
            const doneChips = doneLegChipsRef.current;
            const mapSettings = useSettings.getState().map;
            const unit = getDistanceUnit(useSettings.getState());
            const zoom = m.getZoom();
            const hideZ = mapSettings.legLabelHideZoom ?? 11;
            const mergeZ = mapSettings.legLabelMergeZoom ?? 13;
            // Labels ride with the trail conceptually — hidden together.
            const labelMode: 'full' | 'total' | 'none' =
              mapSettings.showTrails === false || zoom < hideZ
                ? 'none'
                : zoom < Math.max(mergeZ, hideZ)
                  ? 'total'
                  : 'full';
            const wantedChips = new Set<string>();

            // Drones actively RETURNING: dashed green line to the firmware
            // home + distance/ETA chip (operator: "when doing rtl show me
            // dashed line, time to arrival, distance").
            const rtlBirds = Object.entries(state.states).filter(
              ([, rs]) => rs?.mode === 'rtl' && rs.home && rs.position,
            );

            // Active guidance lines (always drawn) + per-leg labels (full).
            if (ids.length > 0 || rtlBirds.length > 0 || gotoLinesDrawn) {
              ensureGotoLineLayer(m);
              const src = m.getSource('goto-lines') as maplibregl.GeoJSONSource | undefined;
              const features: GeoJSON.Feature[] = [];
              for (const [robotId, rs] of rtlBirds) {
                const home = rs!.home!;
                const dronePos = disp.get(robotId) ?? rs!.position!;
                features.push({
                  type: 'Feature',
                  geometry: {
                    type: 'LineString',
                    coordinates: [
                      [dronePos.lon, dronePos.lat],
                      [home.lon, home.lat],
                    ],
                  },
                  properties: { color: '#54b35e' },
                });
                if (labelMode === 'none') continue;
                const d = distMeters(dronePos, home);
                const spd = rs!.velocity?.ground_speed_ms ?? null;
                let text = `RTL · ${fmtDistance(d, unit)}`;
                if (spd != null && spd > 0.3) text += ` · ${Math.round(d / spd)} s`;
                const key = `rtl:${robotId}`;
                let chip = chips.get(key);
                if (!chip) {
                  chip = new maplibregl.Marker({
                    element: makeLegChipEl(false, '#54b35e'),
                    anchor: 'center',
                  })
                    .setLngLat([(dronePos.lon + home.lon) / 2, (dronePos.lat + home.lat) / 2])
                    .addTo(m);
                  chips.set(key, chip);
                }
                chip.setLngLat([(dronePos.lon + home.lon) / 2, (dronePos.lat + home.lat) / 2]);
                orientLegChip(m, chip, dronePos, home, text);
                wantedChips.add(key);
              }
              for (const robotId of ids) {
                const tgt = targets2[robotId]!;
                const dronePos = disp.get(robotId) ?? state.states[robotId]?.position;
                if (!dronePos) continue;
                features.push({
                  type: 'Feature',
                  geometry: {
                    type: 'LineString',
                    coordinates: [
                      [dronePos.lon, dronePos.lat],
                      [tgt.lon, tgt.lat],
                    ],
                  },
                  properties: {},
                });
                if (labelMode !== 'full') continue;
                // Label anchored to the STATIC leg (origin → target), so it
                // doesn't glide with the drone. Text = traveled / planned
                // for the leg (baseline resets when a new GOTO replaces an
                // unfinished one); plain remaining when no origin is known.
                const remaining = distMeters(dronePos, tgt);
                const spd = state.states[robotId]?.velocity?.ground_speed_ms ?? null;
                const anchorA = tgt.from ?? dronePos;
                let text: string;
                // traveled/planned only makes sense while the drone is
                // actually between origin and target. Once it has receded
                // past the origin (alt change, manual flying), the frozen
                // planned figure contradicts the visible line, so fall back
                // to plain remaining distance.
                if (tgt.from && remaining <= distMeters(tgt.from, tgt) * 1.15) {
                  const planned = distMeters(tgt.from, tgt);
                  const traveled = Math.min(planned, Math.max(0, planned - remaining));
                  text = `${fmtDistance(traveled, unit)} / ${fmtDistance(planned, unit)}`;
                } else {
                  text = fmtDistance(remaining, unit);
                }
                if (spd != null && spd > 0.3) text += ` · ${Math.round(remaining / spd)} s`;
                let chip = chips.get(robotId);
                if (!chip) {
                  chip = new maplibregl.Marker({ element: makeLegChipEl(false), anchor: 'center' })
                    .setLngLat([(anchorA.lon + tgt.lon) / 2, (anchorA.lat + tgt.lat) / 2])
                    .addTo(m);
                  chips.set(robotId, chip);
                }
                chip.setLngLat([(anchorA.lon + tgt.lon) / 2, (anchorA.lat + tgt.lat) / 2]);
                orientLegChip(m, chip, anchorA, tgt, text);
                wantedChips.add(robotId);
              }
              if (src) src.setData({ type: 'FeatureCollection', features });
              gotoLinesDrawn = features.length > 0;
            }

            // Merged mode: one Σ-total per asset (history legs + active
            // planned), anchored on the active leg (or the newest done one).
            if (labelMode === 'total') {
              const robotsWithLegs = new Set<string>(ids);
              for (const [rid, legs] of Object.entries(gotoSt.history)) {
                if (legs.some((l) => l.from)) robotsWithLegs.add(rid);
              }
              for (const robotId of robotsWithLegs) {
                let total = 0;
                for (const leg of gotoSt.history[robotId] ?? []) {
                  if (leg.from) total += distMeters(leg.from, leg);
                }
                const tgt = targets2[robotId];
                let a: { lat: number; lon: number } | null = null;
                let b: { lat: number; lon: number } | null = null;
                if (tgt) {
                  const dronePos = disp.get(robotId) ?? state.states[robotId]?.position;
                  a = tgt.from ?? dronePos ?? null;
                  b = tgt;
                  if (a) total += distMeters(a, tgt);
                } else {
                  const newest = (gotoSt.history[robotId] ?? []).find((l) => l.from);
                  if (newest?.from) {
                    a = newest.from;
                    b = newest;
                  }
                }
                if (!a || !b || total <= 0) continue;
                let chip = chips.get(robotId);
                if (!chip) {
                  chip = new maplibregl.Marker({ element: makeLegChipEl(false), anchor: 'center' })
                    .setLngLat([(a.lon + b.lon) / 2, (a.lat + b.lat) / 2])
                    .addTo(m);
                  chips.set(robotId, chip);
                }
                chip.setLngLat([(a.lon + b.lon) / 2, (a.lat + b.lat) / 2]);
                orientLegChip(m, chip, a, b, `Σ ${fmtDistance(total, unit)}`);
                wantedChips.add(robotId);
              }
            }

            // COMPLETED legs: persistent per-leg labels (full mode only) —
            // derived from goto history (persists like the trail), dimmed.
            const wantedDone = new Set<string>();
            if (labelMode === 'full') {
              for (const [robotId, legs] of Object.entries(gotoSt.history)) {
                for (const leg of legs) {
                  if (!leg.from) continue; // pre-`from` legacy entries
                  const key = `${robotId}:${leg.ts_ms}`;
                  wantedDone.add(key);
                  let chip = doneChips.get(key);
                  if (!chip) {
                    chip = new maplibregl.Marker({
                      element: makeLegChipEl(true),
                      anchor: 'center',
                    })
                      .setLngLat([(leg.from.lon + leg.lon) / 2, (leg.from.lat + leg.lat) / 2])
                      .addTo(m);
                    doneChips.set(key, chip);
                  }
                  orientLegChip(m, chip, leg.from, leg, fmtDistance(distMeters(leg.from, leg), unit));
                }
              }
            }

            // Sweep anything this frame didn't claim (mode changes, cleared
            // targets, hidden trails).
            for (const id of Array.from(chips.keys())) {
              if (!wantedChips.has(id)) {
                chips.get(id)?.remove();
                chips.delete(id);
              }
            }
            for (const key of Array.from(doneChips.keys())) {
              if (!wantedDone.has(key)) {
                doneChips.get(key)?.remove();
                doneChips.delete(key);
              }
            }
          }
        }

        // Follow: hard-lock the camera to the interpolated position — no
        // restarted ease animations, just a jump per painted frame.
        if (followCenter) m.jumpTo({ center: followCenter });
      } catch (err) {
        // Never let one bad frame kill the painter — that's how icons
        // froze permanently before.
        console.error('[map] frame update failed', err);
      }

      fleetDirty = false;
      if (needMore) schedule();
    }

    function schedule() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(frame);
    }

    function markDirty() {
      fleetDirty = true;
      schedule();
    }

    markDirty();
    const unsubs = [
      useFleet.subscribe(markDirty),
      useSelection.subscribe(markDirty),
      // Target set/clear must repaint the line even between telemetry ticks.
      useGotoTargets.subscribe(markDirty),
      // Per-asset trail toggle → force a trail re-upload next frame.
      useTrailPrefs.subscribe(() => {
        lastUploadedTrails = null;
        markDirty();
      }),
    ];
    return () => {
      unsubs.forEach((u) => u());
      if (rafId !== null) cancelAnimationFrame(rafId);
      gotoLineLabelsRef.current.forEach((mk) => mk.remove());
      gotoLineLabelsRef.current.clear();
      doneLegChipsRef.current.forEach((mk) => mk.remove());
      doneLegChipsRef.current.clear();
      rtlMarkersRef.current.forEach((mk) => mk.remove());
      rtlMarkersRef.current.clear();
    };
  }, []);

  // Render persistent GOTO target markers. Subscribes to gotoTargets and
  // fleet roster (for the asset name) and rebuilds on either change.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    function render(gt: {
      targets: Record<string, import('@/store/gotoTargets').GotoTarget>;
      queues: Record<string, import('@/store/gotoTargets').GotoTarget[]>;
    }) {
      const targets = gt.targets;
      const markers = gotoMarkersRef.current;
      const robots = useFleet.getState().robots;
      const seen = new Set<string>();
      for (const [robotId, t] of Object.entries(targets)) {
        seen.add(robotId);
        const name = robots[robotId]?.name ?? robotId;
        let mk = markers.get(robotId);
        if (!mk) {
          // Crosshair reticle: two thin bars crossing at (0,0) inside a fixed
          // 20×20 box. MapLibre anchors this box at `center`, so the crossing
          // point sits ON the clicked coord. Nothing that could visually tug
          // the eye vertically (label sits BESIDE, not above).
          const el = document.createElement('div');
          el.className = 'fabricos-goto-marker';
          el.style.cssText = [
            'position:relative',
            'width:20px',
            'height:20px',
            'cursor:pointer',
            'box-sizing:border-box',
          ].join(';');
          el.title = `${name} — click to cancel GOTO (LOITER)`;
          el.addEventListener('click', (ev) => {
            ev.stopPropagation();
            // Full cancel — LOITER + clear. A bare `clear()` here removed
            // the marker while the drone kept flying to the old target
            // (redundancy-audit finding, fixed 2026-07-14).
            cancelGoto(robotId);
          });

          const shadow = '0 0 0 1px rgba(14,15,17,0.9)';
          // Horizontal + vertical bars: 14px long × 2px thick, centred.
          const barH = document.createElement('span');
          barH.style.cssText = [
            'position:absolute',
            'left:3px',
            'top:9px',
            'width:14px',
            'height:2px',
            `background:${COLOR_AMBER}`,
            `box-shadow:${shadow}`,
            'pointer-events:none',
          ].join(';');
          const barV = document.createElement('span');
          barV.style.cssText = [
            'position:absolute',
            'left:9px',
            'top:3px',
            'width:2px',
            'height:14px',
            `background:${COLOR_AMBER}`,
            `box-shadow:${shadow}`,
            'pointer-events:none',
          ].join(';');
          // Centre dot — 4px filled square, so the exact click point is
          // unambiguously marked and stays visible against amber bars.
          const dot = document.createElement('span');
          dot.style.cssText = [
            'position:absolute',
            'left:8px',
            'top:8px',
            'width:4px',
            'height:4px',
            'background:#0e0f11',
            `box-shadow:0 0 0 1px ${COLOR_AMBER}`,
            'pointer-events:none',
          ].join(';');
          // Outer ring — thin amber circle for glance readability at low zoom.
          // Turns red (fence warning) when the target lies outside the soft
          // geofence — see the update pass below.
          const ring = document.createElement('span');
          ring.className = 'goto-ring';
          ring.style.cssText = [
            'position:absolute',
            'left:0',
            'top:0',
            'width:20px',
            'height:20px',
            'border-radius:50%',
            `border:1px solid ${COLOR_AMBER}`,
            'opacity:0.65',
            'pointer-events:none',
            'box-sizing:border-box',
          ].join(';');
          el.appendChild(ring);
          el.appendChild(barH);
          el.appendChild(barV);
          el.appendChild(dot);

          // Info tag sits to the RIGHT of the reticle — vertically centred
          // to the reticle so it doesn't pull the operator's eye off the
          // click point. First line: `→ NAME`. Second line: altitude AGL.
          const label = document.createElement('span');
          label.className = 'goto-label';
          label.style.cssText = [
            'position:absolute',
            'left:calc(100% + 8px)',
            'top:50%',
            'transform:translateY(-50%)',
            'display:flex',
            'flex-direction:column',
            'gap:1px',
            'white-space:nowrap',
            'font-family:var(--font-mono)',
            'font-size:9px',
            'letter-spacing:0.12em',
            'text-transform:uppercase',
            'background:rgba(14,15,17,0.78)',
            'border:1px solid rgba(232,168,58,0.45)',
            `color:${COLOR_AMBER}`,
            'padding:2px 5px',
            'border-radius:2px',
            'pointer-events:none',
            'line-height:1.2',
          ].join(';');
          const labelName = document.createElement('span');
          labelName.className = 'goto-label-name';
          const labelAlt = document.createElement('span');
          labelAlt.className = 'goto-label-alt';
          labelAlt.style.cssText = 'opacity:0.75;font-size:8px;letter-spacing:0.1em';
          // Fence-breach explainer — hidden until the painter flags it.
          const labelFence = document.createElement('span');
          labelFence.className = 'goto-label-fence';
          labelFence.style.cssText =
            'display:none;color:#e5484d;font-size:8px;letter-spacing:0.1em';
          // Distance lives ON the drone→target line (midpoint chip painted
          // by the fleet painter), not here — operator call 2026-07-14.
          label.appendChild(labelName);
          label.appendChild(labelAlt);
          label.appendChild(labelFence);
          el.appendChild(label);

          const marker = new maplibregl.Marker({
            element: el,
            anchor: 'center',
            offset: [0, 0],
          }).setLngLat([t.lon, t.lat]);
          marker.addTo(m!);
          markers.set(robotId, marker);
          mk = marker;
        }
        mk.setLngLat([t.lon, t.lat]);
        const root = mk.getElement();
        const nameEl = root.querySelector('.goto-label-name') as HTMLElement | null;
        const altEl = root.querySelector('.goto-label-alt') as HTMLElement | null;
        if (nameEl) nameEl.textContent = `→ ${name}`;
        if (altEl) altEl.textContent = `${Math.round(t.alt_m)} m AGL`;
        // (Fence colouring lives in the painter — it re-evaluates every
        // frame, so a home that arrives late or a radius change mid-target
        // recolours correctly.)
      }
      // Remove markers whose target has been cleared.
      for (const id of Array.from(markers.keys())) {
        if (!seen.has(id)) {
          markers.get(id)?.remove();
          markers.delete(id);
        }
      }

      // Queued legs (shift+click) — small numbered amber dots. The active
      // target is leg 1; the queue continues 2, 3, … Teardown + rebuild:
      // queues hold a handful of points at most.
      gotoQueueMarkersRef.current.forEach((mk) => mk.remove());
      gotoQueueMarkersRef.current = [];
      for (const queue of Object.values(gt.queues)) {
        queue.forEach((q, i) => {
          const el = document.createElement('div');
          el.style.cssText = [
            'display:grid',
            'place-items:center',
            'width:16px',
            'height:16px',
            'border-radius:50%',
            `border:1.5px dashed ${COLOR_AMBER}`,
            'background:rgba(14,15,17,0.85)',
            `color:${COLOR_AMBER}`,
            'font-family:var(--font-mono)',
            'font-size:8.5px',
            'font-weight:700',
            'box-sizing:border-box',
            'pointer-events:none',
          ].join(';');
          el.textContent = String(i + 2);
          gotoQueueMarkersRef.current.push(
            new maplibregl.Marker({ element: el, anchor: 'center' })
              .setLngLat([q.lon, q.lat])
              .addTo(m!),
          );
        });
      }
    }

    render(useGotoTargets.getState());
    // Live "… m away" updates ride the fleet painter frame (see above),
    // not a second fleet subscription.
    const unsub = useGotoTargets.subscribe((s) => render(s));
    return () => {
      unsub();
      gotoMarkersRef.current.forEach((mk) => mk.remove());
      gotoMarkersRef.current.clear();
      gotoQueueMarkersRef.current.forEach((mk) => mk.remove());
      gotoQueueMarkersRef.current = [];
    };
  }, []);

  // Measure-mode range marker + drone→point line. Hidden (removed / emptied)
  // outside measure mode; the readout lives in MeasureOverlay.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    let marker: maplibregl.Marker | null = null;

    const LINE_SRC = 'pilot-measure-line';
    function ensureMeasureLayer(): maplibregl.GeoJSONSource | undefined {
      if (!m!.isStyleLoaded()) return undefined;
      if (!m!.getSource(LINE_SRC)) {
        m!.addSource(LINE_SRC, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        m!.addLayer({
          id: LINE_SRC,
          type: 'line',
          source: LINE_SRC,
          paint: {
            'line-color': COLOR_ACCENT,
            'line-width': 2,
            'line-dasharray': [2, 2],
            'line-opacity': 0.85,
          },
        });
      }
      return m!.getSource(LINE_SRC) as maplibregl.GeoJSONSource | undefined;
    }

    // Tracks whether the line source currently holds a feature, so idle
    // telemetry ticks bail out without touching the map — a per-tick
    // setData (even with empty data) forces repaints that visibly jank
    // drone-marker movement.
    let lineDrawn = false;

    function sync() {
      const { point } = useMeasure.getState();
      const inMode = useMapMode.getState().mode === 'measure';
      const active = inMode && point != null;

      // Marker at the clicked point.
      if (!active) {
        marker?.remove();
        marker = null;
      } else {
        if (!marker) {
          const el = document.createElement('div');
          el.className = 'fabricos-measure-marker';
          el.style.cssText = [
            'position:relative',
            'width:14px',
            'height:14px',
            'border-radius:50%',
            `border:2px solid ${COLOR_ACCENT}`,
            'background:rgba(14,15,17,0.75)',
            'box-shadow:0 0 0 1px rgba(14,15,17,0.9)',
            'box-sizing:border-box',
          ].join(';');
          // Distance readout pinned AT the point (operator: don't make the
          // eye travel to an overlay). Live — follows the drone.
          const lbl = document.createElement('span');
          lbl.className = 'measure-dist-label';
          lbl.style.cssText = [
            'position:absolute',
            'left:calc(100% + 7px)',
            'top:50%',
            'transform:translateY(-50%)',
            'white-space:nowrap',
            'font-family:var(--font-mono)',
            'font-size:9.5px',
            'letter-spacing:0.1em',
            `color:${COLOR_ACCENT}`,
            'background:rgba(14,15,17,0.85)',
            'border:1px solid rgba(79,200,224,0.35)',
            'border-radius:2px',
            'padding:1px 5px',
            'pointer-events:none',
          ].join(';');
          el.appendChild(lbl);
          marker = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([point!.lon, point!.lat])
            .addTo(m!);
        }
        marker.setLngLat([point!.lon, point!.lat]);
        // Update the pinned distance from the selected drone.
        const lbl = marker.getElement().querySelector('.measure-dist-label') as HTMLElement | null;
        if (lbl) {
          const sel2 = useSelection.getState().selected;
          const rid = sel2.size > 0 ? (Array.from(sel2)[0] as string) : null;
          const dronePos = rid ? useFleet.getState().states[rid]?.position : undefined;
          if (dronePos) {
            const unit = getDistanceUnit(useSettings.getState());
            lbl.textContent = fmtDistance(distMeters(dronePos, point!), unit);
            lbl.style.display = '';
          } else {
            lbl.style.display = 'none';
          }
        }
      }

      // Nothing to draw and nothing drawn → don't touch the map at all.
      if (!active && !lineDrawn) return;

      // Dashed line from the selected drone to the point — follows the drone.
      const src = ensureMeasureLayer();
      if (!src) return;
      if (!active) {
        src.setData({ type: 'FeatureCollection', features: [] });
        lineDrawn = false;
        return;
      }
      const selection = useSelection.getState().selected;
      const robotId = selection.size > 0 ? (Array.from(selection)[0] as string) : null;
      const pos = robotId ? useFleet.getState().states[robotId]?.position : undefined;
      if (!pos) {
        if (lineDrawn) {
          src.setData({ type: 'FeatureCollection', features: [] });
          lineDrawn = false;
        }
        return;
      }
      src.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [pos.lon, pos.lat],
                [point!.lon, point!.lat],
              ],
            },
            properties: {},
          },
        ],
      });
      lineDrawn = true;
    }

    // Rebuild after basemap swaps (styledata wipes custom sources). The
    // source is gone at that point, so reset the drawn flag first.
    function onStyleData() {
      lineDrawn = false;
      sync();
    }

    sync();
    m.on('styledata', onStyleData);
    const unsubs = [
      useMeasure.subscribe(sync),
      useMapMode.subscribe(sync),
      useSelection.subscribe(sync),
      // Fleet ticks arrive at telemetry rate — only re-draw while a
      // measurement is actually on screen.
      useFleet.subscribe(() => {
        if (lineDrawn || (useMapMode.getState().mode === 'measure' && useMeasure.getState().point)) {
          sync();
        }
      }),
    ];
    return () => {
      m.off('styledata', onStyleData);
      unsubs.forEach((u) => u());
      marker?.remove();
      // The map may already be destroyed (unmount order) — getLayer on a
      // removed map throws because its internal style is gone.
      try {
        if (m.getLayer(LINE_SRC)) m.removeLayer(LINE_SRC);
        if (m.getSource(LINE_SRC)) m.removeSource(LINE_SRC);
      } catch {
        /* map already torn down; nothing to remove */
      }
    };
  }, []);

  // Mission waypoint markers + route line on Pilot's map. Renders verbatim
  // what MissionMap renders so the same waypoint looks identical in both
  // workspaces — same colours, same drag-to-move, same click + alt-click
  // semantics, same selected-state. The underlying `useMission` store is
  // shared so edits in either workspace are visible in the other.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    function render(state: ReturnType<typeof useMission.getState>) {
      const markers = waypointMarkersRef.current;
      const seenSeqs = new Set<number>();
      for (const wp of state.waypoints) {
        seenSeqs.add(wp.seq);
        let entry = markers.get(wp.seq);
        if (!entry) {
          const el = document.createElement('div');
          el.className = 'fabricos-wp-marker';
          el.style.cssText =
            'width:22px;height:22px;border-radius:50%;border:2px solid white;' +
            'box-shadow:0 0 0 1px rgba(0,0,0,0.7);font:bold 11px Inter,sans-serif;' +
            'color:#0B0F1A;display:grid;place-items:center;cursor:grab;';
          el.textContent = String(wp.seq + 1);
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            if ((e as MouseEvent).altKey) {
              useMission.getState().removeWaypoint(wp.seq);
            } else {
              useMission.getState().setSelectedSeq(wp.seq);
              useSelection.getState().setFocus('waypoint');
            }
          });
          const marker = new maplibregl.Marker({ element: el, draggable: true })
            .setLngLat([wp.lon, wp.lat]);
          marker.on('dragend', () => {
            const { lat, lng } = marker.getLngLat();
            useMission.getState().moveWaypoint(wp.seq, lat, lng);
          });
          marker.addTo(m!);
          entry = { el, marker };
          markers.set(wp.seq, entry);
        }
        entry.el.textContent = String(wp.seq + 1);
        entry.el.style.background =
          state.selectedSeq === wp.seq ? WP_COLOR_SELECTED : WP_COLOR;
        entry.marker.setLngLat([wp.lon, wp.lat]);
      }
      for (const seq of Array.from(markers.keys())) {
        if (!seenSeqs.has(seq)) {
          markers.get(seq)?.marker.remove();
          markers.delete(seq);
        }
      }
      // Route line + direction arrows. Single render path: ensure the
      // layers exist (no-op if already), then push fresh data to both
      // sources. ensureMissionLayer is idempotent.
      ensureMissionLayer(m!);
      const lineSrc = m!.getSource('pilot-mission-route') as
        | maplibregl.GeoJSONSource
        | undefined;
      const arrowSrc = m!.getSource('pilot-mission-arrows') as
        | maplibregl.GeoJSONSource
        | undefined;

      const coords = state.waypoints.map((w) => [w.lon, w.lat] as [number, number]);

      if (lineSrc) {
        lineSrc.setData({
          type: 'FeatureCollection',
          features:
            coords.length >= 2
              ? [
                  {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: coords },
                    properties: {},
                  },
                ]
              : [],
        });
      }
      if (arrowSrc) {
        // One arrow at the midpoint of every consecutive pair, rotated to
        // the forward bearing so the operator sees the flight direction.
        const arrowFeatures: GeoJSON.Feature<GeoJSON.Point>[] = [];
        for (let i = 1; i < coords.length; i++) {
          const a = coords[i - 1]!;
          const b = coords[i]!;
          const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          arrowFeatures.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: mid },
            properties: { bearing: bearingDeg(a[1], a[0], b[1], b[0]) },
          });
        }
        arrowSrc.setData({ type: 'FeatureCollection', features: arrowFeatures });
      }
    }

    render(useMission.getState());
    const unsub = useMission.subscribe((s) => render(s));
    return () => {
      unsub();
      waypointMarkersRef.current.forEach((m) => m.marker.remove());
      waypointMarkersRef.current.clear();
    };
  }, []);

  return (
    <div ref={containerRef} className={cn('relative h-full w-full', cursorClass)}>
      <MapModeBar />
      <ManualOverlay />
      <MeasureOverlay />
      <ViewOptions />
      {loading && (
        <div className="pointer-events-none absolute right-3 bottom-12 z-10 flex items-center gap-1.5 rounded-full border border-border-default bg-base/85 px-2.5 py-1 font-mono text-[10px] text-fg-secondary backdrop-blur-md">
          <Loader2 className="h-3 w-3 animate-spin" />
          loading map…
        </div>
      )}
      {/* Cursor tooltip — coords + distance/bearing from the selected
          drone, riding next to the pointer. Written by the mousemove
          handler (left/top inline), never via React state. */}
      <div
        ref={hoverHudRef}
        style={{ display: 'none' }}
        className="pointer-events-none absolute z-10 whitespace-nowrap rounded border border-border-default bg-base/90 px-2 py-1 font-mono text-[10px] text-fg-secondary shadow-[var(--shadow-pop)]"
      />
      {ctxMenu && (
        <MapContextMenu
          menu={ctxMenu}
          onClose={() => setCtxMenu(null)}
          onDirectAction={() => useMapMode.getState().touch()}
        />
      )}
    </div>
  );
}

/** Right-click command menu (Normal-idle Measure + Direct modes). Every
 *  entry is a deliberate, point-anchored action — this is the sanctioned
 *  way to command from the inert mode. */
function MapContextMenu({
  menu,
  onClose,
  onDirectAction,
}: {
  menu: { x: number; y: number; lat: number; lon: number };
  onClose: () => void;
  onDirectAction: () => void;
}) {
  const sel = useSelection((s) => s.selected);
  const robotId = sel.size === 1 ? (sel.values().next().value as string) : null;
  const robots = useFleet((s) => s.robots);
  const states = useFleet((s) => s.states);
  const unit = useSettings((s) => getDistanceUnit(s));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pos = robotId ? states[robotId]?.position : undefined;
  const name = robotId ? robots[robotId]?.name ?? robotId : null;
  const dist = pos ? distMeters(pos, { lat: menu.lat, lon: menu.lon }) : null;

  function run(action: () => void) {
    onDirectAction();
    action();
    onClose();
  }

  const itemCls =
    'flex w-full items-center gap-2 px-2.5 py-1.5 text-left font-mono text-[11px] text-fg-secondary hover:bg-surface-3 hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div
      className="absolute z-30 w-52 overflow-hidden rounded border border-border-default bg-surface-1/95 py-1 shadow-[var(--shadow-pop)] backdrop-blur-md"
      style={{
        left: Math.min(menu.x, window.innerWidth - 240),
        top: menu.y,
      }}
    >
      <div className="border-b border-border-subtle px-2.5 py-1.5 font-mono text-[10px] text-fg-muted">
        {fmtLatLon(menu.lat, menu.lon)}
        {dist != null && name && (
          <div className="mt-0.5 text-amber-300/90">
            {fmtDistance(dist, unit)} from {name}
          </div>
        )}
      </div>
      <button
        type="button"
        className={itemCls}
        disabled={!robotId}
        onClick={() => run(() => sendGoto(robotId!, menu.lat, menu.lon))}
      >
        <Crosshair className="h-3 w-3 shrink-0" />
        GOTO here
      </button>
      <button
        type="button"
        className={itemCls}
        disabled={!robotId}
        onClick={() => run(() => sendGoto(robotId!, menu.lat, menu.lon, { append: true }))}
      >
        <ListPlus className="h-3 w-3 shrink-0" />
        Queue GOTO here
      </button>
      <button
        type="button"
        className={itemCls}
        disabled={!robotId}
        onClick={() =>
          run(() => {
            sendCommand({
              kind: 'set_home',
              id: `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
              robot_id: robotId!,
              priority: 'normal',
              lat: menu.lat,
              lon: menu.lon,
            });
            useToasts.getState().push({
              tone: 'good',
              title: `RTL point set for ${name ?? 'asset'}`,
              body: `${menu.lat.toFixed(6)}, ${menu.lon.toFixed(6)}`,
              durationMs: 4000,
            });
          })
        }
      >
        <CornerDownLeft className="h-3 w-3 shrink-0" />
        Set RTL point here
      </button>
      <button
        type="button"
        className={itemCls}
        onClick={() =>
          run(() => {
            void navigator.clipboard?.writeText(
              `${menu.lat.toFixed(6)}, ${menu.lon.toFixed(6)}`,
            );
            useToasts.getState().push({
              tone: 'info',
              title: 'Coordinates copied',
              body: `${menu.lat.toFixed(6)}, ${menu.lon.toFixed(6)}`,
              durationMs: 2500,
            });
          })
        }
      >
        <ClipboardCopy className="h-3 w-3 shrink-0" />
        Copy coordinates
      </button>
    </div>
  );
}

function flashClickMarker(map: MlMap, lngLat: [number, number]) {
  const el = document.createElement('div');
  el.style.cssText =
    'width:24px;height:24px;border-radius:50%;border:2px solid #3B82F6;' +
    'box-shadow:0 0 12px 4px rgba(59,130,246,0.45);pointer-events:none;' +
    'transition:opacity 800ms ease,transform 800ms ease;opacity:1;transform:scale(0.4);';
  const marker = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map);
  requestAnimationFrame(() => {
    el.style.opacity = '0';
    el.style.transform = 'scale(1.4)';
  });
  setTimeout(() => marker.remove(), 900);
}
